
import React, { useState } from 'react';
import { userService } from '../services/userService';
import { Language } from '../types';
import { translations } from '../i18n';
import { GlassCard, Button } from '../components/shared';
import { Rocket, Mail, Lock, User, Loader2, AlertCircle, ArrowRight } from 'lucide-react';

interface AuthViewProps {
  lang: Language;
  onLangToggle: () => void;
}

export const AuthView: React.FC<AuthViewProps> = ({ lang, onLangToggle }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  const t = translations[lang];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isLogin) {
        const { error } = await userService.signIn(email, password);
        if (error) throw error;
      } else {
        const { error } = await userService.signUp(email, password, name);
        if (error) throw error;
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || t.authError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex flex-col items-center justify-center p-6 overflow-hidden bg-slate-50 dark:bg-slate-950 transition-colors duration-500">
      
      {/* 1. Animated Background Ambience (The "Nebula") */}
      <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none z-0">
         {/* Top Right Blob */}
         <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-indigo-300/40 dark:bg-indigo-600/30 rounded-full blur-[100px] animate-blob opacity-60"></div>
         {/* Bottom Left Blob */}
         <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-violet-300/40 dark:bg-violet-600/30 rounded-full blur-[100px] animate-blob animation-delay-2000 opacity-60"></div>
         {/* Floating Middle Blob */}
         <div className="absolute top-[40%] left-[30%] w-[300px] h-[300px] bg-cyan-200/40 dark:bg-cyan-500/20 rounded-full blur-[80px] animate-blob animation-delay-4000 opacity-40"></div>
      </div>

      {/* 2. Main Content Container */}
      <div className="relative z-10 w-full max-w-md flex flex-col items-center gap-8">
        
        {/* Brand Header */}
        <div className="text-center animate-in fade-in slide-in-from-top-8 duration-700">
          <div className="relative inline-block mb-6 group cursor-pointer">
             <div className="absolute inset-0 bg-indigo-500 blur-xl opacity-50 group-hover:opacity-80 transition-opacity duration-500 rounded-full"></div>
             <div className="relative bg-gradient-to-br from-indigo-500 to-violet-600 dark:from-slate-900 dark:to-indigo-950 p-6 rounded-3xl border border-white/20 dark:border-indigo-500/30 shadow-2xl transform group-hover:-translate-y-2 transition-transform duration-500">
                <Rocket size={48} className="text-white drop-shadow-[0_0_10px_rgba(99,102,241,0.8)]" />
             </div>
          </div>
          
          <h1 className="text-5xl font-orbitron font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-800 via-indigo-600 to-violet-600 dark:from-white dark:via-indigo-100 dark:to-indigo-300 mb-2 tracking-wide drop-shadow-sm">
            Wonder<span className="text-indigo-500 dark:text-indigo-400">Verse</span>
          </h1>
          <p className="text-indigo-800 dark:text-indigo-200/80 font-bold uppercase text-xs tracking-[0.4em]">
            {t.tagline}
          </p>
        </div>

        {/* 3. Login Card - Light Glass Variation for Contrast */}
        <GlassCard variant="light" className="w-full backdrop-blur-xl animate-in zoom-in-95 duration-500 border-white/60 shadow-2xl shadow-indigo-200/40 dark:shadow-indigo-900/40">
          
          {/* Language Switcher */}
          <div className="absolute top-6 right-6">
             <button 
               onClick={onLangToggle} 
               className="text-[10px] font-black uppercase tracking-wider text-indigo-500 dark:text-indigo-400 hover:text-indigo-600 transition-colors bg-indigo-50 dark:bg-indigo-500/10 px-2 py-1 rounded-md"
             >
                {lang === 'en' ? '中' : 'EN'}
             </button>
          </div>

          <h2 className="text-2xl font-black text-slate-800 dark:text-white mb-2">
            {isLogin ? t.welcomeBack : t.joinAdventure}
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 font-medium">
             {isLogin ? "Ready to explore the universe?" : "Create your commander profile."}
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3 text-red-600 text-sm animate-in slide-in-from-left-2">
              <AlertCircle size={18} className="shrink-0 mt-0.5" />
              <span className="font-bold">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div className="space-y-1.5 group">
                <label className="text-xs font-black text-slate-400 ml-1 uppercase tracking-wider group-focus-within:text-indigo-500 transition-colors">{t.name}</label>
                <div className="relative">
                  <User className="absolute left-4 top-3.5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={20} />
                  <input 
                    type="text" 
                    value={name}
                    onChange={e => setName(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-900/50 border-2 border-slate-100 dark:border-white/10 rounded-2xl py-3 pl-12 pr-4 text-slate-800 dark:text-white font-bold focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-900 transition-all placeholder:text-slate-300 placeholder:font-normal"
                    placeholder="Commander Name"
                    required
                  />
                </div>
              </div>
            )}

            <div className="space-y-1.5 group">
              <label className="text-xs font-black text-slate-400 ml-1 uppercase tracking-wider group-focus-within:text-indigo-500 transition-colors">{t.email}</label>
              <div className="relative">
                <Mail className="absolute left-4 top-3.5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={20} />
                <input 
                  type="email" 
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-900/50 border-2 border-slate-100 dark:border-white/10 rounded-2xl py-3 pl-12 pr-4 text-slate-800 dark:text-white font-bold focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-900 transition-all placeholder:text-slate-300 placeholder:font-normal"
                  placeholder="name@example.com"
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5 group">
              <label className="text-xs font-black text-slate-400 ml-1 uppercase tracking-wider group-focus-within:text-indigo-500 transition-colors">{t.password}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-3.5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={20} />
                <input 
                  type="password" 
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-900/50 border-2 border-slate-100 dark:border-white/10 rounded-2xl py-3 pl-12 pr-4 text-slate-800 dark:text-white font-bold focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-900 transition-all placeholder:text-slate-300 placeholder:font-normal"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            <div className="pt-2">
              <Button 
                className="w-full py-4 text-lg shadow-xl shadow-indigo-500/20 hover:shadow-indigo-500/40 transform hover:-translate-y-0.5 transition-all" 
                disabled={loading}
              >
                {loading ? <Loader2 className="animate-spin" /> : (
                  <span className="flex items-center gap-2">
                    {isLogin ? t.login : t.signup} <ArrowRight size={20} className="opacity-80"/>
                  </span>
                )}
              </Button>
            </div>
          </form>

          <div className="mt-8 text-center border-t border-slate-100 dark:border-white/10 pt-6">
            <p className="text-slate-400 text-sm font-medium">
              {isLogin ? t.noAccount : t.haveAccount}
              <button 
                onClick={() => { setIsLogin(!isLogin); setError(null); }}
                className="ml-2 font-black text-indigo-600 hover:text-indigo-500 transition-colors underline decoration-2 decoration-transparent hover:decoration-indigo-200 underline-offset-4"
              >
                {isLogin ? t.signup : t.login}
              </button>
            </p>
          </div>
        </GlassCard>

        {/* Footer info */}
        <p className="text-indigo-800/40 dark:text-indigo-200/40 text-xs font-medium">
          Secure Educational Environment • v2.0
        </p>

      </div>
    </div>
  );
};
